import { useMemo } from 'react';
import classNames from 'classnames';
import Button from '@/shared/components/flip-ui-kit/Button';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { type Column, TableV2 } from '@/shared/components/molecules/TableV2';
import DelegatorLinkCell from '@/shared/components/operators/DelegatorLinkCell';
import { getDelegatingSince, getDelegationRewards } from '@/shared/graphql/explorer/delegation';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useOperatorsAndAuction from '@/shared/hooks/useOperatorsAndAuction';
import { type UseExtrinsicMutationResult } from '@/shared/hooks/useStateChainAccount';
import ForbidIcon from '@/shared/icons/flip-ui-kit/small/Forbid';
import { formatPct } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';

export default function DelegatorsTable({
  operatorId,
  blockDelegator,
}: {
  operatorId: `cF${string}`;
  blockDelegator?: UseExtrinsicMutationResult<'operator_blockDelegator'>;
}) {
  const { operators = [] } = useOperatorsAndAuction();
  const columns: Column[] = [
    'Account',
    'Current Delegation',
    'Pending Delegation',
    'Rewards',
    {
      name: 'APY',
      tooltip: 'The APY that the Delegator should earn this epoch.',
    },
    { name: 'Delegating Since', alignment: blockDelegator ? 'left' : 'right' },
  ];
  const operator = operators.find((o) => o.idSs58 === operatorId);

  const { data: activityData } = useGqlQuery(getDelegatingSince, {
    variables: { ids: operator?.delegations.nodes.map((d) => d.delegatorIdSs58) as string[] },
    enabled: operator !== undefined,
  });

  const { data: rewardsData } = useGqlQuery(getDelegationRewards, {
    variables: { ids: operator?.delegations.nodes.map((d) => d.delegatorIdSs58) as string[] },
    enabled: operator !== undefined,
  });

  if (blockDelegator) columns.push('');

  const rowData = useMemo(
    () =>
      operator?.delegations.nodes
        .map(({ delegatorIdSs58, upcomingAmount, activeAmount }) => ({
          rewards: TokenAmount.fromAsset(
            rewardsData?.accounts?.nodes.find((item) => item.idSs58 === delegatorIdSs58)?.rewards
              .nodes[0]?.amount ?? 0,
            'Flip',
          ),
          apy: operator?.currentEpoch.delegators.has(delegatorIdSs58 as `cF${string}`)
            ? operator.currentEpoch.delegatorApy
            : ({ type: 'n/a' } as const),
          delegatingSince: activityData?.accounts?.nodes?.find(
            (acc) => acc.idSs58 === delegatorIdSs58,
          )?.activity.aggregates?.max?.timestamp,
          upcomingAmount: TokenAmount.fromAsset(upcomingAmount, 'Flip'),
          activeAmount: TokenAmount.fromAsset(activeAmount, 'Flip'),
          idSs58: delegatorIdSs58 as `cF${string}`,
        }))
        .sort((a, b) => b.upcomingAmount.cmp(a.upcomingAmount)) ?? [],
    [activityData, rewardsData, operator],
  );

  return (
    <TableV2
      columns={columns}
      rows={rowData.map(
        ({ idSs58: delegatorId, activeAmount, upcomingAmount, rewards, apy, delegatingSince }) => (
          <tr key={delegatorId}>
            <td>
              <div className="flex items-center">
                <DelegatorLinkCell accountId={delegatorId} />
              </div>
            </td>
            <td>
              <div className="flex items-center">
                <FlipLabel amount={activeAmount} />
              </div>
            </td>
            <td>
              <div className="flex items-center">
                <FlipLabel amount={upcomingAmount} />
              </div>
            </td>
            <td>
              <div className="flex items-center">
                <FlipLabel amount={rewards} />
              </div>
            </td>
            <td className="text-cf-white">
              <div className="flex items-center">
                {apy.type === 'apyPct' && formatPct(apy.apyPct)}
              </div>
            </td>
            <td className="text-cf-light-2">
              <div className={classNames('flex items-center', !blockDelegator && 'justify-end')}>
                {delegatingSince ? <AgeWithTooltip timestamp={delegatingSince} /> : '-'}
              </div>
            </td>
            {blockDelegator && (
              <td className="w-[1%]">
                <div className="flex items-center">
                  <Button
                    type="secondary-standard"
                    className="whitespace-nowrap"
                    onClick={() => blockDelegator.mutate(delegatorId)}
                    icon={<ForbidIcon className="mr-1 text-cf-red-2" />}
                    iconPos="left"
                    loading={blockDelegator.isPending && 'iconOnly'}
                    disabled="ifLoading"
                  >
                    {operator?.openDelegationEnabled ? 'Blacklist' : 'Remove from Whitelist'}
                  </Button>
                </div>
              </td>
            )}
          </tr>
        ),
      )}
    />
  );
}
